import { Routes } from '@angular/router';
import { authGuard, teacherGuard, moduleGuard } from '@core/guards/auth.guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: '/dashboard',
    pathMatch: 'full'
  },
  {
    path: 'auth',
    loadChildren: () => import('./features/auth/auth.routes').then(m => m.authRoutes)
  },
  {
    path: 'business-registration',
    loadComponent: () => import('./features/business/registration/components/registration.component').then(m => m.RegistrationComponent)
  },
  {
    path: 'individuals-registration',
    loadComponent: () => import('./features/individuals/components/registration/registration-form.component').then(m => m.RegistrationFormComponent)
  },
  {
    path: 'individuals-registration-v2',
    loadComponent: () => import('./features/individuals/components/registration/registration-form.component').then(m => m.RegistrationFormComponent)
  },
  // Public coordinator email-action pages (BE-111) — no auth, no layout shell.
  // Path matches the live email link base (app-config permission_action_base_url),
  // e.g. https://portal.dev.wakiapp.com/dismissal/permission-requests/actions/{reject,approve}?token=
  {
    path: 'dismissal/permission-requests/actions',
    loadChildren: () => import('./features/permission-actions/permission-actions.routes')
      .then(m => m.permissionActionsRoutes)
  },
  // Public per-branch "Pay Fees" portal — no auth, no layout shell. The link a
  // school embeds on its own website: parents enter their phone / a student's
  // national ID and see this branch's students with pending / paid amounts.
  // Canonical link is /pay/<branch-name-slug>/<branchId> (name is cosmetic; the
  // id resolves). Public `/public/...` endpoints are already allow-listed in the
  // interceptors.
  {
    path: 'pay',
    loadChildren: () => import('./features/payment-portal/payment-portal.routes')
      .then(m => m.paymentPortalRoutes)
  },
  // Business Management Routes (outside dashboard nesting)
  {
    path: 'businesses',
    canActivate: [authGuard, teacherGuard],
    loadComponent: () => import('./layout/main-layout/main-layout.component')
      .then(m => m.MainLayoutComponent),
    data: {
      breadcrumb: { label: 'businesses', route: '/businesses' }
    },
    children: [
      {
        path: 'general',
        loadChildren: () => import('./features/business/business.routes')
          .then(m => m.businessRoutes)
      },
      // Admin finance pages moved to /admin/finance/* — forward the old
      // /businesses/finance paths. Must precede the `finance` lazy mount so
      // these exact deep paths match before the module's prefix match.
      {
        path: 'finance/transaction-logs',
        redirectTo: '/admin/finance/transaction-logs',
        pathMatch: 'full',
      },
      {
        path: 'finance/money-requests',
        redirectTo: '/admin/finance/money-requests',
        pathMatch: 'full',
      },
            {
        path: 'finance',
        canMatch: [moduleGuard],
        data: { module: 'finance' },
        loadChildren: () =>
          import('./features/finance/finance.routes').then(
            (m) => m.financeRoutes
          ),
      },
      // Sales Routes
      {
        path: 'sales',
        canMatch: [moduleGuard],
        data: { module: 'sales' },
        loadChildren: () => import('./features/sales/sales.routes')
              .then(m => m.salesRoutes)
      },
      // Inventory Routes
      {
        path: 'inventory',
        canMatch: [moduleGuard],
        data: { module: 'inventory' },
        loadChildren: () => import('./features/inventory/inventory.routes')
          .then(m => m.inventoryRoutes)
      },
      // Marketing Routes
      {
        path: 'marketing',
        canMatch: [moduleGuard],
        data: { module: 'marketing' },
        loadChildren: () => import('./features/marketing/marketing.routes')
          .then(m => m.marketingRoutes)
      },
      // Events Routes (excluded from BE-288 module scoping — admin surface)
      {
        path: 'events',
        loadChildren: () => import('./features/events/events.routes')
          .then(m => m.eventsRoutes)
      },
      // Dismissal Routes
      {
        path: 'dismissal-call',
        canMatch: [moduleGuard],
        data: { module: 'dismissal-call' },
        loadChildren: () => import('./features/dismissal/dismissal.routes')
          .then(m => m.dismissalRoutes)
      },
      // Membership Service Routes (Withdrawal Requests, Bank Transfers)
      {
        path: 'membership-service',
        loadChildren: () => import('./features/membership-service/membership-service.routes')
          .then(m => m.membershipServiceRoutes)
      },
      // Attendance Routes
      {
        path: 'attendance',
        canMatch: [moduleGuard],
        data: { module: 'attendance' },
        loadChildren: () => import('./features/attendance/attendance.routes')
          .then(m => m.attendanceRoutes)
      },
      // Advertisements Routes (excluded from BE-288 module scoping — admin surface)
      {
        path: 'advertisements',
        loadChildren: () => import('./features/advertisements/advertisements.routes')
          .then(m => m.advertisementsRoutes)
      },
      // Notifications Routes
      {
        path: 'notifications',
        canMatch: [moduleGuard],
        data: { module: 'notifications' },
        loadChildren: () => import('./features/notifications/notifications.routes')
          .then(m => m.notificationsRoutes)
      },
      // Users Routes
      {
        path: 'users',
        canMatch: [moduleGuard],
        data: { module: 'users' },
        loadChildren: () => import('./features/users/users.routes')
          .then(m => m.usersRoutes)
      },
      // Branches Management Section (FE-91)
      {
        path: 'branches',
        canMatch: [moduleGuard],
        data: { module: 'branches' },
        loadChildren: () => import('@features/branches/branches-section.routes')
          .then(m => m.branchesSectionRoutes)
      },
    ]
  },
  // Marketplace Routes
  {
    path: 'marketplace',
    canActivate: [authGuard],
    loadComponent: () => import('./layout/main-layout/main-layout.component')
      .then(m => m.MainLayoutComponent),
    children: [
      {
        path: '',
        loadChildren: () => import('./features/marketplace/marketplace.routes')
          .then(m => m.marketplaceRoutes)
      }
    ]
  },
  // Admin Routes
  {
    path: 'admin',
    canActivate: [authGuard, teacherGuard],
    loadComponent: () => import('./layout/main-layout/main-layout.component')
      .then(m => m.MainLayoutComponent),
    children: [
      // Admin > Finance (Transaction Logs, Money Requests) — reuses the finance
      // feature so its URL reads /admin/finance/* to match the Admin sidebar
      // group and breadcrumb (business finance pages stay at /businesses/finance).
      {
        path: 'finance',
        loadChildren: () =>
          import('./features/finance/finance.routes').then((m) => m.financeRoutes),
      },
      // Admin > Marketing (Coupons) — reuses the marketing feature so its URL reads
      // /admin/marketing/* to match the Admin sidebar group and breadcrumb, exactly
      // as /admin/finance/* does. Coupon approval is a Waki-admin surface: a business
      // creating a coupon raises a request that is reviewed from here.
      // No moduleGuard: /admin is already admin-only via its own guards, and the
      // guard's `marketing` module id belongs to the business-side mount.
      {
        path: 'marketing',
        loadChildren: () =>
          import('./features/marketing/marketing.routes').then((m) => m.marketingRoutes),
      },
      {
        path: '',
        loadChildren: () => import('./features/admin/admin.routes')
          .then(m => m.adminRoutes)
      }
    ]
  },
  // Business Management Routes (outside dashboard nesting)
  {
    path: 'individuals',
    canActivate: [authGuard],
    loadComponent: () => import('./layout/main-layout/main-layout.component')
      .then(m => m.MainLayoutComponent),
    data: { 
      breadcrumb: { label: 'Individuals', route: '/individuals' }
    },
    children: [
      {
        path: 'general',
        loadChildren: () => import('./features/individuals/individuals.routes')
          .then(m => m.individualsRoutes)
      },
      {
        path: 'finance',
        loadChildren: () => import('./features/individuals-finance/individuals-finance.routes')
          .then(m => m.financeRoutes)
      }
    ]
  },
  // Branches Routes - Now handled under /business/branches
  // {
  //   path: 'branches',
  //   canActivate: [authGuard],
  //   loadComponent: () => import('./layout/main-layout/main-layout.component')
  //     .then(m => m.MainLayoutComponent),
  //   data: { 
  //     breadcrumb: { label: 'Branches', route: '/branches' }
  //   },
  //   children: [
  //     {
  //       path: '',
  //       loadChildren: () => import('./features/branches/branches.routes')
  //         .then(m => m.branchesRoutes)
  //     }
  //   ]
  // },
  // Main Dashboard and other features
  {
    path: 'dashboard',
    canActivate: [authGuard, teacherGuard],
    loadComponent: () => import('./layout/main-layout/main-layout.component')
      .then(m => m.MainLayoutComponent),
    children: [
      // Dashboard Overview
      {
        path: '',
        loadChildren: () => import('./features/dashboard/dashboard.routes')
          .then(m => m.dashboardRoutes)
      }
    ]
  },
  // WAKI Store Routes
  {
    path: 'waki-store',
    canActivate: [authGuard],
    loadComponent: () => import('./layout/main-layout/main-layout.component')
      .then(m => m.MainLayoutComponent),
    children: [
      {
        path: '',
        loadChildren: () => import('./features/waki-store/waki-store.routes')
          .then(m => m.wakiStoreRoutes)
      }
    ]
  },
  // School Management Routes
  {
    path: 'school',
    canActivate: [authGuard],
    loadComponent: () => import('./layout/main-layout/main-layout.component')
      .then(m => m.MainLayoutComponent),
    children: [
      {
        path: '',
        loadChildren: () => import('./features/school-management/school-management.routes')
          .then(m => m.schoolRoutes)
      }
    ]
  },
  // Standalone fullscreen Teacher Dismissal filter (no sidebar/main-layout shell).
  // Teacher Portal Users land here after login; Business/Branch are locked to
  // their assigned scope, Group/Section/Gender remain editable.
  {
    path: 'dismissal-call/filter',
    canActivate: [authGuard],
    loadComponent: () => import('./features/dismissal/components/call-setup/dismissal-call-setup.component')
      .then(m => m.DismissalCallSetupComponent)
  },
  // Standalone fullscreen Dismissal Call Screen (no sidebar/main-layout shell)
  {
    path: 'dismissal-call/cards',
    canActivate: [authGuard],
    loadComponent: () => import('./features/dismissal/components/call-screen/dismissal-call-screen.component')
      .then(m => m.DismissalCallScreenComponent)
  },
  {
    path: '**',
    redirectTo: '/dashboard'
  }
];
